import { useState } from 'react';
import {
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  Grid,
  KpiCard,
  PageHeader,
  TableCard,
  Tag,
  type Column,
} from '@sparkery/ui';
// Money is added in integer minor units by the SAME functions the ledger uses. The
// local helper here was `Math.round(Number(amount) * 100)` — a float multiply, on the
// one screen whose purpose is letting a partner check the arithmetic.
import { sumMoney, toMinor, type Statement } from '@sparkery/contracts';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useViewer } from '../../lib/viewer.tsx';
import { money } from '../../lib/report.tsx';
import { periodLabel, useMarkStatementPaid, useStatements } from '../../lib/statements.ts';
import { ApiError } from '../../lib/api.ts';
import { StatementPanel } from './StatementPanel.tsx';
import './statements.css';

/**
 * Commission statements, one row per partner per month.
 *
 * A statement is a closed month: the monthly run produced it and froze it, and nothing
 * that happens afterwards changes it — a refund on an order it settled becomes a
 * recovery on the NEXT statement. The view is written to make that legible rather than
 * to hide it, because "why is last month still 1,605.60 when the order was refunded" is
 * the question this screen exists to answer.
 *
 * Staff additionally see who has not been paid and can record a payment. Partners see
 * the same figures with no payment controls, which follows from the permission rather
 * than from a branch here.
 */
export function StatementsView() {
  const { viewer, can } = useViewer();
  const statements = useStatements();
  const canManage = can('settlement:manage');

  // Which statement is open lives in the URL, so a support conversation can link
  // straight to the month being queried.
  const { open } = useSearch({ from: '/statements' });
  const navigate = useNavigate();
  const toggle = (statementId: string) =>
    void navigate({
      to: '/statements',
      search: { open: open === statementId ? undefined : statementId },
    });

  const rows = statements.data?.statements ?? [];
  const outstanding = rows.filter((row) => row.status === 'generated');
  const isStaff = statements.data?.partnerId === null;

  return (
    <>
      <PageHeader
        title="Statements"
        subtitle={
          viewer.partner
            ? `${viewer.partner.name} · commission by month`
            : 'Every partner, newest month first'
        }
      />

      {statements.isError ? (
        <ErrorState
          title="Could not load statements"
          {...(statements.error instanceof Error ? { detail: statements.error.message } : {})}
          onRetry={() => void statements.refetch()}
        />
      ) : null}

      <Grid cols={3}>
        <KpiCard
          label="Awaiting payment"
          value={statements.data ? money(sumOf(outstanding, 'payable')) : '—'}
          meta={
            outstanding.length === 1
              ? '1 statement not yet marked paid'
              : `${outstanding.length} statements not yet marked paid`
          }
          iconName="commission"
          tint="amber"
        />
        <KpiCard
          label="Paid to date"
          value={
            statements.data
              ? money(
                  sumOf(
                    rows.filter((row) => row.status === 'paid'),
                    'payable',
                  ),
                )
              : '—'
          }
          meta="Across every statement on record"
          iconName="sales"
          tint="ink"
        />
        <KpiCard
          label="Carried forward"
          value={statements.data ? money(carriedNow(rows)) : '—'}
          // Two captions, because at zero the explanation of a negative balance
          // describes something that has not happened — which reads as a warning.
          meta={
            carriedNow(rows) === '0.00'
              ? 'Nothing outstanding against future commission'
              : 'Recovered more than was earned — clears against future commission'
          }
          iconName="conversion"
          tint="pink"
        />
      </Grid>

      <TableCard
        title="Monthly statements"
        meta={statements.data ? `${rows.length} on record` : undefined}
      >
        <DataTable
          caption="Commission statements by month"
          columns={columnsFor({ isStaff, canManage })}
          rows={rows}
          rowKey={(row) => row.id}
          loading={statements.isPending}
          onRowClick={(row) => toggle(row.id)}
          empty={
            <EmptyState
              title="No statements yet"
              body="Statements are produced on the first of each month."
            />
          }
        />
      </TableCard>

      {/* Rendered outside the table rather than as an expanded row: the movements have
          their own columns, and squeezing a second table into one cell of the first
          makes both unreadable. */}
      {open ? <StatementPanel statementId={open} /> : null}
    </>
  );
}

function columnsFor(options: { isStaff: boolean; canManage: boolean }): Column<Statement>[] {
  const columns: Column<Statement>[] = [
    { id: 'period', header: 'Month', cell: (row) => periodLabel(row.period) },
  ];

  // Only for staff: a partner's every row says their own name, which is noise.
  if (options.isStaff) {
    columns.push({ id: 'partner', header: 'Partner', cell: (row) => row.partnerName });
  }

  columns.push(
    {
      id: 'earned',
      header: 'Earned',
      numeric: true,
      cell: (row) => money(row.earned.amount),
    },
    {
      id: 'against',
      header: 'Adjusted',
      numeric: true,
      secondary: true,
      // Adjustments and recoveries are added together in one column: both reduce the
      // month, and the difference between them (was it paid out yet?) is a settlement
      // detail nobody reading this table is asking about.
      cell: (row) => deductionCell(row),
    },
    {
      id: 'opening',
      header: 'Brought forward',
      numeric: true,
      secondary: true,
      cell: (row) =>
        row.openingBalance.amount === '0.00' ? '—' : money(row.openingBalance.amount),
    },
    {
      id: 'payable',
      header: 'Payable',
      numeric: true,
      cell: (row) => <strong>{money(row.payable.amount)}</strong>,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (row) =>
        row.status === 'paid' ? (
          <Tag tone="confirmed">Paid</Tag>
        ) : (
          <Tag tone="pending">Payable</Tag>
        ),
    },
    {
      id: 'reference',
      header: 'Reference',
      secondary: true,
      cell: (row) =>
        row.status === 'paid' ? (
          <span className="statementsReference">{row.paidReference ?? '—'}</span>
        ) : options.canManage ? (
          // The row itself opens the drill-down, so this cell stops its clicks: an
          // admin reaching for the reference field should not also collapse the panel.
          <span
            className="statementsStopClick"
            onClick={(event) => event.stopPropagation()}
            role="presentation"
          >
            <MarkPaid statement={row} />
          </span>
        ) : (
          <span className="statementsPendingHint">Not yet paid</span>
        ),
    },
  );

  return columns;
}

/**
 * The mark-as-paid control: a reference, then a button.
 *
 * Inline rather than a dialog, and the reference is required rather than optional — a
 * statement marked paid with nothing to trace it to cannot be reconciled against a bank
 * statement, which is the only reason to record it at all.
 */
function MarkPaid({ statement }: { statement: Statement }) {
  const [open, setOpen] = useState(false);
  const [reference, setReference] = useState('');
  const markPaid = useMarkStatementPaid();

  if (!open) {
    return (
      <Button variant="ghost" size="small" onClick={() => setOpen(true)}>
        Mark paid
      </Button>
    );
  }

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!reference.trim()) return;
    markPaid.mutate({ statementId: statement.id, reference: reference.trim() });
  };

  return (
    <form className="statementsPay" onSubmit={submit}>
      <label className="statementsPayLabel" htmlFor={`ref-${statement.id}`}>
        Payment reference
      </label>
      <input
        id={`ref-${statement.id}`}
        className="control"
        value={reference}
        onChange={(event) => setReference(event.target.value)}
        placeholder="BACS-0031"
        autoComplete="off"
        spellCheck={false}
      />
      <div className="statementsPayActions">
        <Button type="submit" size="small" disabled={!reference.trim() || markPaid.isPending}>
          {markPaid.isPending ? 'Recording…' : `Paid ${money(statement.payable.amount)}`}
        </Button>
        <Button variant="ghost" size="small" type="button" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
      {markPaid.isError ? (
        <p className="statementsPayError" role="alert">
          {markPaid.error instanceof ApiError
            ? markPaid.error.message
            : 'Could not record that payment.'}
        </p>
      ) : null}
    </form>
  );
}

function deductionCell(row: Statement) {
  const total = sumMoney([row.adjusted.amount, row.recovered.amount]);
  if (toMinor(total) === 0) return '—';

  // The recovery is called out where there is one, because it is the surprising half:
  // money already paid out on an order the customer has since sent back.
  return (
    <span className="statementsDeduction">
      {money(total)}
      {toMinor(row.recovered.amount) === 0 ? null : (
        <span className="statementsRecovered">incl. {money(row.recovered.amount)} recovered</span>
      )}
    </span>
  );
}

function sumOf(rows: Statement[], field: 'payable'): string {
  return sumMoney(rows.map((row) => row[field].amount));
}

/**
 * The balance still carried, taken from each partner's LATEST statement only.
 *
 * Summing every row's carry-forward would count the same debit once per month it
 * survived — the figure people want is what is still outstanding now.
 */
function carriedNow(rows: Statement[]): string {
  const latest = new Map<string, Statement>();
  for (const row of rows) {
    const held = latest.get(row.partnerId);
    if (!held || row.period > held.period) latest.set(row.partnerId, row);
  }

  return sumMoney([...latest.values()].map((row) => row.carriedForward.amount));
}
